import { FC, useCallback, useMemo, useState } from "react";
import { addHours, format } from "date-fns";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Routes } from "router";
import { Button, Divider, Select, TextField, Typography } from "theme";

import { Box, Grid2, MenuItem, useTheme } from "@mui/material";
import { styled } from "@mui/system";

import { useBookingById } from "../../../api/hooks/useBookingById/useBookingById.ts";
import { useCreateBooking } from "../../../api/hooks/useCreateBooking/useCreateBooking.ts";
import { usePromocode } from "../../../api/hooks/usePromocode/usePromocode.ts";
import { useUpdateBooking } from "../../../api/hooks/useUpdateBooking/useUpdateBooking.ts";
import { InvertButton } from "../../../theme/components/Button/Button.tsx";
import { PrepaymentOptions } from "../../../utils/constants/prepayment-options.ts";
import { useBooking } from "../../../utils/context/Booking/BookingContext.tsx";

import EditPrice from "./components/EditPrice.tsx";
import SelectedEquipment from "./components/SelectedEquipment.tsx";
import SelectedHall from "./components/SelectedHall.tsx";
import { useIsBookingDataChanged } from "./hooks/useIsBookingDataChanged.tsx";

const StyledButton = styled("button")(({ theme }) => ({
  background: "none",
  border: "none",
  cursor: "pointer",
  borderWidth: 1,
  borderStyle: "solid",
  borderRadius: "5px",
  borderColor: "transparent",
  transition: "all 0.3s",
  fontWeight: 600,
  padding: theme.spacing(0.5, 1),

  "&:hover:not(:disabled)": {
    color: "#4654c9",
    borderColor: theme.palette.secondary.light,
  },
}));

type SummaryFormProps = {
  setStep: (step: number) => void;
}

const SummaryForm: FC<SummaryFormProps> = ({ setStep }) => {
  const { id } = useParams();
  const theme = useTheme();
  const { palette } = useTheme();
  const navigate = useNavigate();
  const { booking, equipment, setBooking, setEquipment } = useBooking();
  const createBookingProvider = useCreateBooking();
  const updateBookingProvider = useUpdateBooking(id || "");
  const validatePromoCodeProvider = usePromocode();
  const isNewBooking = id === "new";
  const { data: bookingData } = useBookingById();

  const [equipmentPrice, setEquipmentPrice] = useState(0);
  const [hallPrice, setHallPrice] = useState(0);
  const [promoCodeId, setPromoCodeId] = useState("");
  const [promoCode, setPromoCode] = useState<{
    promoCodeId: string;
    promoCodeAmount: number;
    validTo: string;
  } | null>(null);
  const [prepaymentOption, setPrepaymentOption] = useState(PrepaymentOptions.NONE);

  const prepaymentOptions = useMemo(() => [
    {
      label: "Без передоплати",
      value: PrepaymentOptions.NONE,
    },
    {
      label: "передоплата 50%",
      value: PrepaymentOptions.HALF,
    },
    {
      label: "передоплата 100%",
      value: PrepaymentOptions.FULL,
    },
  ], []);

  const startDate = new Date((booking?.slotDate.includes("Z") ? booking?.slotDate.slice(0, -1) : booking?.slotDate) || Date.now());
  const endDate = addHours(startDate, booking?.hoursNumber || 0);

  const validatePromoCode = useCallback(() => {
    validatePromoCodeProvider.mutateAsync(promoCodeId).then((response) => {
      setPromoCode(response);
      toast.success("Промокод успішно застосовано");
    });
  }, [validatePromoCodeProvider, promoCodeId]);

  const createBooking = useCallback(() => {
    if (booking) {
      const formData = {
        hallId: booking.hall,
        startDate: format(new Date(startDate), "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'"),
        endDate: format(new Date(endDate), "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'"),
        equipments: equipment ? equipment.equipment.map((el) => ({
          id: el.id,
          startDate: format(new Date(startDate), "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'"),
          endDate: format(new Date(addHours(startDate, el.range)), "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'"),
          count: el.count,
        })) : [],
        customerName: booking.name,
        customerEmail: booking.email,
        customerPhone: booking.phone,
        customerComment: booking.comment,
        notifyBySmsHours: 2,
        //TODO: remove
        redirectUrl: `https://booking.panorama-studio.com.ua/booking/{bookingId}`,
        defaultEquipment: booking.lightSetup,
        ...(isNewBooking && {
          bookingPrepaymentValue: prepaymentOption,
        }),
        ...(promoCode && { promocodeId: promoCode.promoCodeId }),
        ...(isNewBooking && {
          sendBookingDetailsSms: booking.sendBookingDetailsSms,
          smsText: booking.smsText,
        })
      };
      if (isNewBooking) {
        createBookingProvider
          .mutateAsync(formData)
          .then(() => {
            toast.success("Бронювання успішно створене");
            setBooking(null);
            setEquipment(null);
            navigate(Routes.Root);
          })
          .catch((e) => {
            toast(e);
          });
      } else {
        updateBookingProvider
          .mutateAsync(formData)
          .then(() => {
            toast.success("Бронювання успішно оновлено");
            setBooking(null);
            setEquipment(null);
            navigate(Routes.Root);
          })
          .catch((e) => {
            toast(e);
          });
      }
    }
  }, [booking, startDate, endDate, equipment, isNewBooking, prepaymentOption, promoCode, createBookingProvider, setBooking, setEquipment, navigate, updateBookingProvider]);

  const price = useMemo(() => {
    if (promoCode) {
      return equipmentPrice + hallPrice - promoCode.promoCodeAmount < 0 ? 0 : equipmentPrice + hallPrice - promoCode.promoCodeAmount;
    }
    return equipmentPrice + hallPrice - Number(bookingData?.discountFromPromocode || 0);
  }, [equipmentPrice, hallPrice, promoCode, bookingData?.discountFromPromocode]);

  const { isBookingDataChanged } = useIsBookingDataChanged();

  return (
    <Box my={3.5}>
      <Typography mb={4.5} variant="h1" textTransform="uppercase" weight="semiBold">
        Підсумок
      </Typography>

      <Grid2 mb={5} container>
        <Box maxWidth="335px" width="100%">
          <Typography mb={2.5} variant="h2" textTransform="uppercase" weight="semiBold">
            Інформація про бронювання
          </Typography>
          <Box mb={3}>
            <SelectedEquipment setEquipmentPrice={setEquipmentPrice} />
          </Box>
          <Box mb={2}>
            <SelectedHall setHallPrice={setHallPrice} />
          </Box>

          {isNewBooking && <Box my={2}>
            <Select
              fullWidth
              MenuProps={{
                sx: {
                  "& .MuiPaper-root": {
                    background: palette.primary.main,
                  },
                },
              }}
              value={prepaymentOption}
              onChange={(e) => {
                setPrepaymentOption(e.target.value as any);
              }}
            >
              {prepaymentOptions.map((el) => {
                return (
                  <MenuItem key={el.value} value={el.value}>
                    {el.label}
                  </MenuItem>
                );
              })}
            </Select>
          </Box>}

          {!bookingData?.discountFromPromocode &&
            <TextField disabled={!!bookingData?.discountFromPromocode} InputProps={{
              endAdornment: <StyledButton onClick={validatePromoCode}
                                          disabled={!promoCodeId || validatePromoCodeProvider.isPending}>Застосувати</StyledButton>,
            }} type="text" name="discount" label="Промокод" placeholder="Введіть промокод" value={promoCodeId}
                       onChange={e => setPromoCodeId(e.target.value)} />}

          <Box my={2}>
            <Divider />
          </Box>

          <Grid2 mb={1.5} container justifyContent="space-between" alignItems="center">
            <Typography variant="body1" textTransform="uppercase" weight="semiBold">
              Сума послуг:
            </Typography>
            <Typography variant="body1" textTransform="uppercase" weight="semiBold">
              {(isNewBooking || isBookingDataChanged) ? (equipmentPrice + hallPrice) : bookingData?.totalPrice} грн
            </Typography>
          </Grid2>
          {!!(promoCode || bookingData?.discountFromPromocode) &&
            <Grid2 container justifyContent="space-between" alignItems="center">
              <Typography style={{
                color: theme.palette.success.main,
              }} variant="body1" textTransform="uppercase" weight="semiBold">
                Сума знижки:
              </Typography>
              <Typography style={{
                color: theme.palette.success.main,
              }} variant="body1" textTransform="uppercase" weight="semiBold">
                {Number(promoCode?.promoCodeAmount || 0) + (bookingData ? Number(bookingData?.discountFromPromocode) : 0)} грн
              </Typography>
            </Grid2>}

          <Box my={2}>
            <Divider />
          </Box>

          <Grid2 container justifyContent="space-between" alignItems="center">
            <Typography variant="body1" textTransform="uppercase" weight="bold">
              До оплати:
            </Typography>
            <Grid2 container alignItems="center" gap={1}>
              {!isNewBooking && !isBookingDataChanged && <EditPrice key={price} initialPrice={price} />}
              <Typography variant="body1" textTransform="uppercase" weight="bold">
                {(isNewBooking || isBookingDataChanged) ? price : bookingData?.totalPriceWithDiscounts} грн
              </Typography>
            </Grid2>
          </Grid2>


        </Box>
      </Grid2>

      <Grid2 container justifyContent="space-between">
        <Button type="button" look="normal" onClick={() => setStep(2)}>
          Назад
        </Button>

        {isNewBooking && <InvertButton loading={createBookingProvider.isPending} onClick={createBooking}>
          Створити бронювання
        </InvertButton>}
        {!isNewBooking && <InvertButton loading={createBookingProvider.isPending} onClick={createBooking}>
          Оновити бронювання
        </InvertButton>}
      </Grid2>
    </Box>
  );
};

export default SummaryForm;
